import { gql, QueryResult, useQuery } from '@apollo/client'
import { AggregateBenefactorAttributionsQuery, PifAttribution } from '@/types/codegen-federation'

export const getAttributionInfo = gql`
  query getAttributionInfo($projectSlug: String!) {
    pifAttributionInfo(projectSlug: $projectSlug) {
      benefactorAttributions {
        edges {
          node {
            id
          }
        }
      }
    }
  }
`

export const getThankYouNotesQuery = gql`
  query getThankYouNotes($first: Int, $mine: Boolean) {
    thankYouNotes(first: $first, mine: $mine) {
      edges {
        node {
          authorLocation
          id
          publishDate
          thankYouNote
          totalLikes
          userProfile {
            city
            country
            firstName
            image
            lastName
            state
          }
        }
      }
    }
  }
`

export const getDirectImpactQuery = gql`
  query getDirectImpact {
    directImpact {
      totalPeople
      peopleByCountry {
        count
        country
      }
    }
  }
`

export const aggregateBenefactorAttributions = gql`
  fragment PifAttribution on PifAttribution {
    id
    modified
    projectSlug
    recipient {
      profile {
        country
        firstName
        image
      }
    }
    recipientLocation
    thankyouNote
  }

  query aggregateBenefactorAttributions($first: Int) {
    chosen: pifAttributionInfo(projectSlug: "the-chosen") {
      benefactorAttributions(first: $first) {
        edges {
          node {
            ...PifAttribution
          }
        }
      }
    }
    db: pifAttributionInfo(projectSlug: "dry-bar-best-of") {
      benefactorAttributions(first: $first) {
        edges {
          node {
            ...PifAttribution
          }
        }
      }
    }
    tt: pifAttributionInfo(projectSlug: "tuttle-twins") {
      benefactorAttributions(first: $first) {
        edges {
          node {
            ...PifAttribution
          }
        }
      }
    }
    jb: pifAttributionInfo(projectSlug: "jungle-beat") {
      benefactorAttributions(first: $first) {
        edges {
          node {
            ...PifAttribution
          }
        }
      }
    }
    rd: pifAttributionInfo(projectSlug: "riot") {
      benefactorAttributions(first: $first) {
        edges {
          node {
            ...PifAttribution
          }
        }
      }
    }
    fl: pifAttributionInfo(projectSlug: "freelancers") {
      benefactorAttributions(first: $first) {
        edges {
          node {
            ...PifAttribution
          }
        }
      }
    }
    tm: pifAttributionInfo(projectSlug: "testament") {
      benefactorAttributions(first: $first) {
        edges {
          node {
            ...PifAttribution
          }
        }
      }
    }
    wf: pifAttributionInfo(projectSlug: "wingfeather-saga") {
      benefactorAttributions(first: $first) {
        edges {
          node {
            ...PifAttribution
          }
        }
      }
    }
    pe: pifAttributionInfo(projectSlug: "playing-for-eternity") {
      pifAttributionCount
      benefactorAttributions(first: $first) {
        edges {
          node {
            ...PifAttribution
          }
        }
      }
    }
    hos: pifAttributionInfo(projectSlug: "his-only-son") {
      pifAttributionCount
      benefactorAttributions(first: $first) {
        edges {
          node {
            ...PifAttribution
          }
        }
      }
    }
    lw: pifAttributionInfo(projectSlug: "lightwise") {
      pifAttributionCount
      benefactorAttributions(first: $first) {
        edges {
          node {
            ...PifAttribution
          }
        }
      }
    }
    sof: pifAttributionInfo(projectSlug: "sound-of-freedom") {
      benefactorAttributions(first: $first) {
        edges {
          node {
            ...PifAttribution
          }
        }
      }
    }
  }
`

export interface Attribution {
  benefactorAttributions: {
    edges: ThankYouNote[]
  }
}

export interface ThankYouNotes {
  thankYouNotes: {
    edges: ThankYouNote[]
  }
}

export interface ThankYouNote {
  node: {
    authorLocation: string
    id: string
    publishDate: string
    thankYouNote: string
    totalLikes: number
    userProfile: {
      city: string
      country: string
      firstName: string
      image: string
      lastName: string
      state: string
    }
  }
}

export interface DirectImpact {
  directImpact: {
    totalPeople: number
    peopleByCountry: {
      count: number
      country: string
    }[]
  }
}

export const MAX_NOTES = 1000

const ValidKeys = ['chosen', 'db', 'fl', 'hos', 'jb', 'lw', 'pe', 'rd', 'sof', 'tm', 'tt', 'wf'] as const

type ValidKey = typeof ValidKeys[number]

function isValidKey(key: string): key is ValidKey {
  return ValidKeys.includes(key as ValidKey)
}

export const normalizeBenefactorAttributions = ({
  attributionBenefactorData,
  loadingBenefactorAttribution,
}: {
  attributionBenefactorData?: AggregateBenefactorAttributionsQuery
  loadingBenefactorAttribution?: boolean
}): PifAttribution[] => {
  const result: PifAttribution[] = []

  if (!loadingBenefactorAttribution && attributionBenefactorData) {
    for (const key in attributionBenefactorData) {
      if (isValidKey(key)) {
        const attributions = attributionBenefactorData[key]?.benefactorAttributions?.edges || []
        attributions.forEach((edge) => {
          if (edge?.node?.thankyouNote) {
            result.push(edge.node as PifAttribution)
          }
        })
      }
    }
  }

  return result
}

export const useAttributions = (): QueryResult<Attribution, { first: number }> => {
  return useQuery(getAttributionInfo, { variables: { first: MAX_NOTES }, errorPolicy: 'all' })
}

export const useThankYouNotes = (): QueryResult<ThankYouNotes, { first: number; mine: true }> => {
  // Load in a set max and display > MAX_NOTES if array length equal that
  return useQuery(getThankYouNotesQuery, { variables: { first: MAX_NOTES, mine: true }, errorPolicy: 'all' })
}

export const useDirectImpact = (): QueryResult<DirectImpact> => {
  return useQuery(getDirectImpactQuery, {
    errorPolicy: 'all',
  })
}

export const useBenefactorAttribution = (): QueryResult<AggregateBenefactorAttributionsQuery, { first: number }> => {
  return useQuery(aggregateBenefactorAttributions, { variables: { first: MAX_NOTES }, errorPolicy: 'all' })
}
